/* eslint-disable no-console */
import t from '@rcm/i18n-client';
import type { ChangeEvent } from 'react';
import React, { useState, useEffect, useMemo } from 'react';
import { Form, InputTextField, Button, SubmitButton } from 'forms';
import type { AxiosError } from 'axios';
import axios from 'axios';
import LoadingSpinner from 'loading-spinner/LoadingSpinner';
import './MyProfileForm.css';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateEmail,
  updateUsername,
  updateDaytimePhone,
  updateCellPhone,
  updateShippingPostalCode,
  updateShippingCity,
  updateShippingName,
  updateShippingAddress1,
  updateShippingAddress2,
  updateShippingPhone,
  updateShippingState,
  updateShippingCountry,
  updateBillingPostalCode,
  updateBillingCity,
  updateBillingCountry,
  updateBillingAddress1,
  updateBillingAddress2,
  updateBillingState,
  updatePassword,
  updatePasswordTwo,
  fetchProfileData,
} from './features/myProfileSlice';

interface Props {}

const MyProfileForm = (props: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const appStoreData: any = useSelector((state) => state);
  const [cities, setCities] = useState<{ city: string; label: string }[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>(null);
  const [success, setSuccess] = useState(false);
  const [hidePassword, setHidePassword] = useState(true);
  const [stateCodesShipping, setStateCodesShipping] = useState({});
  const [countriesShipping, setCountriesShipping] = useState<object>([]);
  const [geoCodeRequired, setGeoCodeRequired] = useState(true);
  const [countryWithStatesShipping, setCountryWithStatesShipping] = useState(true);
  const [stateCodesBilling, setStateCodesBilling] = useState({});
  const [countriesBilling, setCountriesBilling] = useState<object>([]);
  const [countryWithStatesBilling, setCountryWithStatesBilling] = useState(true);
  const passwordsMatch = useMemo(
    () => appStoreData.password === appStoreData.passwordTwo,
    [appStoreData.password, appStoreData.passwordTwo]
  );
  const onlyNumbers = (num) => {
    return num.replace(/[^\d.]/g, '');
  };

  // profile field update handlers

  /*
  To do:
  (maybe) add check for shipping same as billing
  make another API call after the update so the freshest data is pulled
  change label for username if it's empty (?)
  */

  const handleChangeUsername = (value: string) => {
    dispatch(updateUsername(value));
  };
  const handleChangeEmail = (value: string) => {
    dispatch(updateEmail(value));
  };
  const handleChangeDaytimePhone = (value: string) => {
    dispatch(updateDaytimePhone(onlyNumbers(value)));
  };
  const handleChangeCellPhone = (value: string) => {
    dispatch(updateCellPhone(onlyNumbers(value)));
  };
  const handleChangePassword = (value: string) => {
    dispatch(updatePassword(value));
  };
  const handleChangePasswordTwo = (value: string) => {
    dispatch(updatePasswordTwo(value));
  };

  const handleChangeShippingPostalCode = (value: string) => {
    dispatch(updateShippingPostalCode(value));
  };
  const handleChangeShippingName = (value: string) => {
    dispatch(updateShippingName(value));
  };
  const handleChangeShippingAddress1 = (value: string) => {
    dispatch(updateShippingAddress1(value));
  };
  const handleChangeShippingAddress2 = (value: string) => {
    dispatch(updateShippingAddress2(value));
  };
  const handleChangeShippingPhone = (value: string) => {
    dispatch(updateShippingPhone(onlyNumbers(value)));
  };
  const handleChangeShippingCitySelect = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch(updateShippingCity(e.target.value));
  };
  const handleChangeShippingCityInput = (value: string) => {
    dispatch(updateShippingCity(value));
  };
  const handleChangeShippingState = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch(updateShippingState(e.target.value));
  };
  const handleChangeShippingCountry = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch(updateShippingCountry(e.target.value));
  };

  const handleChangeBillingCountry = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch(updateBillingCountry(e.target.value));
  };
  const handleChangeBillingPostalCode = (value: string) => {
    dispatch(updateBillingPostalCode(value));
  };
  const handleChangeBillingAddress1 = (value: string) => {
    dispatch(updateBillingAddress1(value));
  };
  const handleChangeBillingAddress2 = (value: string) => {
    dispatch(updateBillingAddress2(value));
  };
  const handleChangeBillingCity = (value: string) => {
    dispatch(updateBillingCity(value));
  };
  const handleChangeBillingState = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch(updateBillingState(e.target.value));
  };

  const handleTogglePasswordForm = () => {
    setHidePassword(!hidePassword);
  };
  const handleUpdateProfile = () => {
    updateProfile(appStoreData);
  };

  useEffect(() => {
    // populates all fields with Vista data
    dispatch(fetchProfileData());
  }, [dispatch]);

  useEffect(() => {
    // get cities related to Postal Code
    if (appStoreData?.shipAddress?.postalCode?.length >= 5 && geoCodeRequired) {
      axios
        .get('/api/resource/geo-code?filter%5Bwhere%5D%5Bzip%5D=' + appStoreData.shipAddress.postalCode)
        .then(function (response) {
          setErrorMsg(null);
          setCities(response.data);
        })
        .catch(function (error: AxiosError) {
          console.log(error.message);
          setErrorMsg(error.message);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStoreData.shipAddress.postalCode]);

  useEffect(() => {
    // get list of states (shipping address) when we receive shipping info
    if (appStoreData?.shipAddress?.country) {
      axios
        .get('/api/resource/country-rules/' + appStoreData.shipAddress.country)
        .then(function (response) {
          setStateCodesShipping(response.data.allowedStates);
          setCountriesShipping(response.data.allowedCountries);
          setGeoCodeRequired(response.data.geoCodeRequired);
          setCountryWithStatesShipping(response.data.countryWithStates);
        })
        .catch(function (error: AxiosError) {
          console.log(error.message);
          setErrorMsg(error.message);
        });
    }

    if (appStoreData?.billAddress?.country) {
      // get list of states (billing address)
      axios
        .get('/api/resource/country-rules/' + appStoreData.billAddress.country)
        .then(function (response) {
          setStateCodesBilling(response.data.allowedStates);
          setCountriesBilling(response.data.allowedCountries);
          setGeoCodeRequired(response.data.geoCodeRequired);
          setCountryWithStatesBilling(response.data.countryWithStates);
        })
        .catch(function (error: AxiosError) {
          console.log(error.message);
          setErrorMsg(error.message);
        });
    }
  }, [appStoreData.billAddress.country, appStoreData.shipAddress.country]);

  function updateProfile(data: object) {
    if (passwordsMatch === false) {
      const element = document.getElementById('password-two');
      element.scrollIntoView({ block: 'center' });
      return;
    }
    setSubmitting(true);
    axios
      .put('/api/checkout/profile/current', data)
      .then(function (response) {
        setSubmitting(false);
        if (response.status == 200) {
          setSuccess(true);
          setErrorMsg(null);
        }
      })
      .catch(function (error: AxiosError) {
        setSubmitting(false);
        console.log(error.message);
        setErrorMsg(error.message);
      });
    window.scrollTo(0, 0);
  }

  // shared elements

  const cityDropdown = (cityData: { city: string; label: string }[]) => {
    return cityData.map((city) => (
      <option value={city.city} key={city.city}>
        {city.label}
      </option>
    ));
  };

  const areaDropdown = (states: object) => {
    return Object.entries(states).map(([k, v]) => (
      <option value={k} key={k}>
        {v}
      </option>
    ));
  };

  const profileUpdated = (shouldShow: boolean) => {
    if (!shouldShow) return null;

    return (
      <div className="alert alert-success">
        <div className="message">{t('Your information was updated successfully!')}</div>
      </div>
    );
  };

  const errorNotice = (message: string) => {
    if (!message) return null;

    return (
      <div className="alert alert-warning" role="alert">
        <div className="message">
          {t('Some information was missing or invalid on the form. Please check the form and try again.')}
        </div>
      </div>
    );
  };

  const shipCityField = () => {
    if (!geoCodeRequired) {
      return (
        <InputTextField
          required={true}
          label={t('City')}
          value={appStoreData?.shipAddress?.city}
          onValueChange={handleChangeShippingCityInput}
        />
      );
    }

    if (geoCodeRequired && countryWithStatesShipping && cities.length > 0) {
      return (
        <>
          <label>{t('City')}</label>
          <select
            className="form-control"
            value={appStoreData.shipAddress.city}
            onChange={handleChangeShippingCitySelect}
          >
            {cityDropdown(cities)}
          </select>
        </>
      );
    }
    return null;
  };

  const pgpvInfo = () => {
    return (
      <div className={`${appStoreData.status !== 'D' ? `hidden` : ''} row`}>
        <div className="col-xs-12">
          <form className="checkout-skinny-form-page-wrap form-group">
            <h1>{t('Personal Group Point Volume (PGPV)')}</h1>
            <table className="table">
              <tr className="pgpv">
                <td>{t('Current Month')}</td>
                <td className="pgpv-amount">{appStoreData.periodPgPv}</td>
              </tr>
              <tr className="pgpv">
                <td>{t('Accumulated Lifetime')}</td>
                <td className="pgpv-amount">{appStoreData.lifetimePgPv}</td>
              </tr>
            </table>
          </form>
        </div>
      </div>
    );
  };

  const personalProfile = () => {
    return (
      <div className="form-group">
        <h1>{t('Personal Profile')}</h1>
        <InputTextField disabled label={t('RCN')} value={String(appStoreData?.rcn)} />
        <InputTextField
          id="email"
          required={true}
          label={t('Email Address')}
          value={appStoreData?.email}
          onValueChange={handleChangeEmail}
        />
        <InputTextField
          id="primary-phone"
          required={true}
          label={t('Primary Phone')}
          value={appStoreData?.daytimePhone}
          onValueChange={handleChangeDaytimePhone}
        />
        <InputTextField
          label={t('Mobile Phone (required for text messaging with the Reliv Mobile App)')}
          value={appStoreData?.cellPhone}
          onValueChange={handleChangeCellPhone}
        />
        <InputTextField
          id="username"
          required={true}
          label={t('Create a Username (letters and numbers only)')}
          value={appStoreData?.username}
          onValueChange={handleChangeUsername}
        />
        <div hidden={!hidePassword} className="password-button">
          <Button
            btn="default"
            onClick={handleTogglePasswordForm}
            text={hidePassword ? t('Change Password') : t('Hide Change Password')}
          />
        </div>

        <div hidden={hidePassword}>
          <InputTextField
            id="password"
            required={false}
            type="password"
            label={t('New Password (letters & numbers only)')}
            value={appStoreData?.password}
            onValueChange={handleChangePassword}
          />
          <InputTextField
            id="password-two"
            error={!passwordsMatch && appStoreData?.password?.length > 0}
            required={false}
            type="password"
            label={t('Verify Password')}
            value={appStoreData?.passwordTwo}
            onValueChange={handleChangePasswordTwo}
          />
        </div>
      </div>
    );
  };

  const shippingAddress = () => {
    return (
      <div className="form-group">
        <h1>{t('Shipping Address')}</h1>
        <div className="form-group">
          <label className="control-label">{t('Country')}</label>
          <select
            className="form-control"
            onChange={handleChangeShippingCountry}
            value={appStoreData?.billAddress?.country}
          >
            {areaDropdown(countriesShipping)}
          </select>
        </div>
        <InputTextField
          required={true}
          label={t('Name')}
          value={appStoreData?.shipAddress?.nameLine}
          onValueChange={handleChangeShippingName}
        />
        <InputTextField
          required={true}
          label={t('Address Line 1')}
          value={appStoreData?.shipAddress?.addressLine1}
          onValueChange={handleChangeShippingAddress1}
        />
        <InputTextField
          required={false}
          label={t('Address Line 2')}
          value={appStoreData?.shipAddress?.addressLine2}
          onValueChange={handleChangeShippingAddress2}
        />
        <InputTextField
          error={
            !appStoreData?.shipAddress?.postalCode ||
            (geoCodeRequired &&
              appStoreData?.shipAddress?.postalCode.length !== 5 &&
              appStoreData?.shipAddress?.postalCode.length !== 9)
          }
          required={false}
          label={t('Postal Code')}
          value={appStoreData?.shipAddress?.postalCode}
          onValueChange={handleChangeShippingPostalCode}
        />
        <div className="form-group">{shipCityField()}</div>
        {countryWithStatesShipping && !geoCodeRequired ? (
          <div className="form-group">
            <label>{t('State')}</label>
            <select
              value={appStoreData?.shipAddress?.state}
              className="form-control"
              onChange={handleChangeShippingState}
            >
              {areaDropdown(stateCodesShipping)}
            </select>
          </div>
        ) : null}
        <InputTextField
          required={true}
          label={t('Phone')}
          value={appStoreData?.shipAddress?.phone}
          onValueChange={handleChangeShippingPhone}
        />
      </div>
    );
  };

  const billingAddress = () => {
    return (
      <div className="form-group">
        <h1>{t('Mailing Address')}</h1>
        <div className="form-group">
          <label className="control-label">{t('Country')}</label>
          <select
            value={appStoreData?.billAddress?.country}
            className="form-control"
            onChange={handleChangeBillingCountry}
            disabled
          >
            {areaDropdown(countriesBilling)}
          </select>
        </div>
        <InputTextField
          required={false}
          label={t('Postal Code')}
          value={appStoreData?.billAddress?.postalCode}
          onValueChange={handleChangeBillingPostalCode}
        />
        <InputTextField
          required={true}
          label={t('Address Line 1')}
          value={appStoreData?.billAddress?.addressLine1}
          onValueChange={handleChangeBillingAddress1}
        />
        <InputTextField
          required={false}
          label={t('Address Line 2')}
          value={appStoreData?.billAddress?.addressLine2}
          onValueChange={handleChangeBillingAddress2}
        />
        <InputTextField
          required={true}
          label={t('City')}
          value={appStoreData?.billAddress?.city}
          onValueChange={handleChangeBillingCity}
        />
        {countryWithStatesBilling ? (
          <div className="form-group">
            <label>{t('State')}</label>
            <select
              value={appStoreData?.billAddress?.state}
              className="form-control"
              onChange={handleChangeBillingState}
            >
              {areaDropdown(stateCodesBilling)}
            </select>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={`${submitting ? `edit-profile` : ''}`}>
      <Form onSubmit={handleUpdateProfile}>
        {profileUpdated(success && !errorMsg)}
        {errorNotice(errorMsg)}
        {pgpvInfo()}
        {personalProfile()}
        {shippingAddress()}
        {billingAddress()}
        <SubmitButton disabled={submitting} className="profile-update-button btn btn-primary">
          <span>{t('Update')}</span>
        </SubmitButton>
        {submitting ? (
          <div className="submitting-form">
            <LoadingSpinner />
          </div>
        ) : null}
      </Form>
    </div>
  );
};
export default MyProfileForm;
